// Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
// See LICENSE in the project root for license information.
import { AnsiSgrParser } from './ansiSgrParser';
import globalStyles from '../styles/global.module.css';
import terminalStyles from '../styles/terminalPane.module.css';
export function createTerminalPaneController(refs) {
    const ansiParser = new AnsiSgrParser();
    _wireLayoutInit(refs.terminalEl);
    _wireClearButton(refs.terminalBody, refs.termClearBtn);
    _wireResizer(refs.terminalEl, refs.resizerEl);
    _wireToggle(refs);
    return {
        appendChunk(kind, text) {
            _appendChunk(refs, ansiParser, kind, text);
        }
    };
}
function _appendChunk(refs, ansiParser, kind, text) {
    const { terminalEl, terminalBody, termAutoScrollCheckbox } = refs;
    if (!terminalBody)
        return;
    const raw = String(text || '');
    const segments = ansiParser.process(raw);
    if (segments && segments.length) {
        for (const seg of segments) {
            const span = document.createElement('span');
            span.className = `${terminalStyles.termChunk} ${kind === 'stderr' ? terminalStyles.stderr : terminalStyles.stdout}`;
            if (seg.style)
                span.setAttribute('style', seg.style);
            span.textContent = seg.text;
            terminalBody.appendChild(span);
        }
    }
    if (!termAutoScrollCheckbox || termAutoScrollCheckbox.checked) {
        terminalBody.scrollTop = terminalBody.scrollHeight;
    }
    if (terminalEl && !terminalEl.classList.contains(terminalStyles.hidden)) {
        terminalEl.classList.add(terminalStyles.termFlash);
        setTimeout(() => terminalEl.classList.remove(terminalStyles.termFlash), 350);
    }
}
function _wireLayoutInit(terminalEl) {
    try {
        if (terminalEl) {
            terminalEl.style.top = '';
        }
    }
    catch (_a) {
        // no-op
    }
}
function _wireClearButton(terminalBody, termClearBtn) {
    if (!terminalBody || !termClearBtn)
        return;
    termClearBtn.addEventListener('click', () => {
        terminalBody.innerHTML = '';
    });
}
function _wireResizer(terminalEl, resizerEl) {
    if (!terminalEl || !resizerEl)
        return;
    const terminalWithState = terminalEl;
    let dragging = false;
    let startX = 0;
    let startWidth = 0;
    const minW = 120;
    const maxW = Math.max(240, window.innerWidth - 200);
    resizerEl.addEventListener('pointerdown', (e) => {
        dragging = true;
        startX = e.clientX;
        startWidth = terminalWithState.getBoundingClientRect().width;
        resizerEl.setPointerCapture(e.pointerId);
        document.body.style.userSelect = 'none';
    });
    window.addEventListener('pointermove', (e) => {
        if (!dragging)
            return;
        const dx = startX - e.clientX;
        let newW = startWidth + dx;
        newW = Math.max(minW, Math.min(maxW, newW));
        terminalWithState._savedWidth = newW;
        if (!terminalWithState.classList.contains(terminalStyles.hidden)) {
            terminalWithState.style.width = newW + 'px';
            terminalWithState.style.flex = '0 0 ' + newW + 'px';
        }
    });
    window.addEventListener('pointerup', (e) => {
        if (!dragging)
            return;
        dragging = false;
        try {
            resizerEl.releasePointerCapture(e.pointerId);
        }
        catch (_a) {
            // ignore mismatched pointer state
        }
        document.body.style.userSelect = '';
    });
    resizerEl.tabIndex = 0;
    resizerEl.addEventListener('keydown', (e) => {
        const step = 16;
        const rect = terminalWithState.getBoundingClientRect();
        let w = rect.width;
        if (e.key === 'ArrowLeft')
            w = Math.max(minW, w - step);
        else if (e.key === 'ArrowRight')
            w = Math.min(maxW, w + step);
        terminalWithState._savedWidth = w;
        if (!terminalWithState.classList.contains(terminalStyles.hidden)) {
            terminalWithState.style.width = w + 'px';
            terminalWithState.style.flex = '0 0 ' + w + 'px';
        }
    });
}
function _wireToggle(refs) {
    const { toggleTerminalBtn, terminalEl, resizerEl, termAutoscrollBtn, termAutoScrollCheckbox } = refs;
    if (!toggleTerminalBtn || !terminalEl)
        return;
    const terminalWithState = terminalEl;
    toggleTerminalBtn.addEventListener('click', () => {
        const currentlyHidden = terminalWithState.classList.contains(terminalStyles.hidden);
        if (currentlyHidden) {
            terminalWithState.classList.remove(terminalStyles.hidden);
            if (resizerEl)
                resizerEl.classList.remove(terminalStyles.hidden);
            if (terminalWithState._savedWidth) {
                terminalWithState.style.width = terminalWithState._savedWidth + 'px';
                terminalWithState.style.flex = '0 0 ' + terminalWithState._savedWidth + 'px';
            }
            else {
                terminalWithState.style.width = '';
                terminalWithState.style.flex = '';
            }
            if (resizerEl)
                resizerEl.tabIndex = 0;
            toggleTerminalBtn.setAttribute('aria-pressed', 'true');
            toggleTerminalBtn.classList.add(globalStyles.active);
        }
        else {
            try {
                terminalWithState._savedWidth = terminalWithState.getBoundingClientRect().width;
            }
            catch (_a) {
                // no-op
            }
            terminalWithState.classList.add(terminalStyles.hidden);
            if (resizerEl) {
                resizerEl.classList.add(terminalStyles.hidden);
                resizerEl.tabIndex = -1;
            }
            terminalWithState.style.width = '';
            terminalWithState.style.flex = '';
            toggleTerminalBtn.setAttribute('aria-pressed', 'false');
            toggleTerminalBtn.classList.remove(globalStyles.active);
        }
    });
    const isVisible = !terminalWithState.classList.contains(terminalStyles.hidden);
    toggleTerminalBtn.setAttribute('aria-pressed', isVisible ? 'true' : 'false');
    if (isVisible)
        toggleTerminalBtn.classList.add(globalStyles.active);
    else
        toggleTerminalBtn.classList.remove(globalStyles.active);
    if (termAutoscrollBtn && termAutoScrollCheckbox) {
        termAutoscrollBtn.setAttribute('aria-pressed', termAutoScrollCheckbox.checked ? 'true' : 'false');
        termAutoscrollBtn.addEventListener('click', () => {
            const newVal = !termAutoScrollCheckbox.checked;
            termAutoScrollCheckbox.checked = newVal;
            termAutoscrollBtn.setAttribute('aria-pressed', newVal ? 'true' : 'false');
        });
    }
}
//# sourceMappingURL=terminalPane.js.map